import React, { useEffect, 
    // useMemo,
     useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Divider,
  Stack,
  // Grid,
  CircularProgress,
  Alert,
  Paper,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  // ListItemText,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import Editor from '@monaco-editor/react';
import useLabQuestionStore from '../../store/useLabQuestionStore';
import useCodeStore from '../../store/useCodeStore';
import useExecutorStore from '../../store/useExecutorStore';

const StudentIDE = () => {
  const { labInternalId, questionId } = useParams();
  const navigate = useNavigate();

  const [authorized, setAuthorized] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [locked, setLocked] = useState(false);
  const [projectFiles, setProjectFiles] = useState({});
  const [activeFile, setActiveFile] = useState('');
  const [runOutput, setRunOutput] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [accessError, setAccessError] = useState(null);

  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(0);

//   const headers = useMemo(() => ({ 'Content-Type': 'application/json' }), []);

  useEffect(() => {
    let cancelled = false;

    console.log("Lab Internal Id", labInternalId, questionId)

    async function bootstrap() {
      setLoading(true);
      setError(null);
      setAccessError(null);
      try {
        const { checkAccess, fetchDetails } = {
          checkAccess: useLabQuestionStore.getState().checkAccess,
          fetchDetails: useLabQuestionStore.getState().fetchDetails,
        };
        const access = await checkAccess(labInternalId, questionId);
        console.log(access)
        if (cancelled) return;
        if (!access?.data?.authorized) {
          const authVal = access?.authorized ?? access?.data?.authorized;
          const reasonVal = access?.reason || access?.data?.reason;
          setAuthorized(Boolean(authVal));
          setAccessError(reasonVal || 'Unauthorized access');
          setLoading(false);
          return;
        }
        setAuthorized(true);

        const q = await fetchDetails(labInternalId, questionId);
        if (cancelled) return;
        setQuestion(q);
        setLocked(Boolean(q?.lockedAfterSubmission));

        const saved = await useCodeStore.getState().loadCode(labInternalId, questionId);
        if (cancelled) return;
        if (saved) {
          const initialFile = 'solution.js';
          setProjectFiles({ [initialFile]: saved });
          setActiveFile(initialFile);
        } else {
          const initialFile = 'solution.js';
          setProjectFiles({ [initialFile]: '// Write your solution here\n' });
          setActiveFile(initialFile);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [labInternalId, questionId]);

  useEffect(() => {
    // Auto-save entire project structure every 10 seconds while not locked
    if (locked || !activeFile || Object.keys(projectFiles).length === 0) return;
    
    clearInterval(saveTimerRef.current);
    saveTimerRef.current = setInterval(async () => {
      try {
        const now = Date.now();
        if (now - lastSavedRef.current < 9500) return;
        setSaving(true);
        // Save the current active file content to backend
        const currentCode = projectFiles[activeFile] || '';
        await useCodeStore.getState().saveCode(labInternalId, questionId, currentCode);
        lastSavedRef.current = Date.now();
      } catch {
        // ignore save errors
      } finally {
        setSaving(false);
      }
    }, 10000);
    return () => clearInterval(saveTimerRef.current);
  }, [projectFiles, activeFile, locked, labInternalId, questionId]);

  const onRun = async () => {
    setError(null);
    setRunOutput(null);
    setSubmitResult(null);
    try {
      const currentCode = projectFiles[activeFile] || '';
      const out = await useExecutorStore.getState().run({ labInternalId, questionId, language: 'javascript', code: currentCode });
      setRunOutput(out);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Run failed');
    }
  };

  const onSubmit = async () => {
    setError(null);
    setSubmitResult(null);
    try {
      const currentCode = projectFiles[activeFile] || '';
      const payload = await useExecutorStore.getState().submit({ labInternalId, questionId, language: 'javascript', code: currentCode });
      setSubmitResult(payload);
      setLocked(Boolean(payload?.lockedAfterSubmission));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Submit failed');
    }
  };

  if (authorized === false) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            403 - Access Denied
          </Typography>
          <Typography>
            {accessError || 'You do not have access to this question.'}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          startIcon={<SaveIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (loading || authorized === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Question Panel */}
      <Box
        sx={{
          width: '50%',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        <Card sx={{ m: 2, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h5" component="div" sx={{ mb: 2 }}>
              {question?.title}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={`Difficulty: ${question?.difficulty}`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Marks: ${question?.marks}`}
                variant="outlined"
                color="primary"
                size="small"
              />
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                color: 'text.secondary',
              }}
            >
              {question?.description}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Code Editor Panel */}
      <Box
        sx={{
          width: '50%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Toolbar */}
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
          elevation={0}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={onRun}
            disabled={locked}
            size="small"
          >
            Run
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileUploadIcon />}
            onClick={onSubmit}
            disabled={locked}
            size="small"
          >
            Submit
          </Button>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {locked ? (
              <Chip
                icon={<LockIcon />}
                label="Locked"
                color="warning"
                variant="outlined"
                size="small"
              />
            ) : saving ? (
              <Chip
                icon={<CircularProgress size={16} />}
                label="Saving..."
                size="small"
              />
            ) : (
              <Chip
                icon={<SaveIcon />}
                label="Saved"
                color="success"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Paper>

        {/* Editor */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Editor
            height="50vh"
            language="javascript"
            theme="vs-dark"
            value={projectFiles[activeFile] ?? ''}
            onChange={(val) => {
              if (activeFile) {
                setProjectFiles(prev => ({ ...prev, [activeFile]: val ?? '' }));
              }
            }}
            options={{ readOnly: locked, minimap: { enabled: false } }}
          />
        </Box>

        {/* Output Panel */}
        <Paper
          sx={{
            flex: 1,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
          }}
          elevation={0}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Console Output
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {runOutput && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>
                  Visible Tests: {runOutput.passedCount}/{runOutput.totalCount}
                </strong>
              </Typography>
              <List dense>
                {(runOutput.visibleCases || []).map((tc, idx) => (
                  <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{tc.name}</strong>
                      </Typography>
                      <Chip
                        label={tc.status}
                        size="small"
                        color={tc.status === 'PASS' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Stack>
                    {tc.output && (
                      <Paper
                        sx={{
                          backgroundColor: '#1e1e1e',
                          color: '#00ff00',
                          p: 1,
                          width: '100%',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          overflow: 'auto',
                        }}
                      >
                        {tc.output}
                      </Paper>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {submitResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Final Score:</strong> {submitResult.finalScore}
              </Typography>
            </Alert>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StudentIDE;